import { Subscription, filter } from 'rxjs';
import { LiveUpdateEventTypes, liveStatusTranslationKey, marketplaceOperationsTopic } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';

export { liveStatusTranslationKey as liveStatusKey };

export function compareSortValues(left: string | number | boolean | null | undefined, right: string | number | boolean | null | undefined): number {
  const leftValue = left ?? '';
  const rightValue = right ?? '';
  if (leftValue === rightValue) {
    return 0;
  }
  return leftValue > rightValue ? 1 : -1;
}

export function toggleSortField<T extends string>(
  currentField: T,
  field: T,
  currentOrder: 1 | -1,
  setField: (value: T) => void,
  setOrder: (value: 1 | -1) => void
): void {
  if (currentField === field) {
    setOrder(currentOrder === 1 ? -1 : 1);
    return;
  }

  setField(field);
  setOrder(1);
}

export function downloadCsv(filename: string, header: string[], rows: string[][]): void {
  const lines = rows.map(row =>
    row.map(value => `"${value.replace(/"/g, '""')}"`).join(','));
  const csv = [header.join(','), ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export interface MarketplaceLiveSubscription {
  topic: string | null;
  eventsSubscription: Subscription | null;
}

export async function subscribeMarketplaceOperationsLiveUpdates(
  tenantId: string | null | undefined,
  liveUpdateService: LiveUpdateService,
  onRefresh: () => void
): Promise<MarketplaceLiveSubscription> {
  if (!tenantId) {
    return { topic: null, eventsSubscription: null };
  }

  const topic = marketplaceOperationsTopic(tenantId);
  await liveUpdateService.subscribe(topic);
  const eventsSubscription = liveUpdateService.events$
    .pipe(filter(event => event.eventType === LiveUpdateEventTypes.marketplaceOperationsChanged))
    .subscribe(() => onRefresh());

  return { topic, eventsSubscription };
}

export async function unsubscribeMarketplaceOperationsLiveUpdates(
  liveUpdateService: LiveUpdateService,
  subscription: MarketplaceLiveSubscription
): Promise<void> {
  subscription.eventsSubscription?.unsubscribe();
  if (subscription.topic) {
    await liveUpdateService.unsubscribe(subscription.topic);
  }
}

export const MARKETPLACE_LIST_TOOLBAR_STYLES = `
  .panel { display: grid; gap: 12px; }
  .toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
  .info { margin: 0; color: #166534; font-weight: 600; }
  .filters { border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 12px; background: #f8fafc; }
  .filters > summary { cursor: pointer; font-weight: 600; }
  .filter-grid { display: grid; gap: 8px; margin-top: 8px; }
  .filter-grid label { display: grid; gap: 4px; font-size: 0.875rem; }
  .filter-actions { display: flex; gap: 8px; flex-wrap: wrap; }
  .live-status { font-size: 0.75rem; padding: 2px 8px; border-radius: 999px; border: 1px solid #cbd5e1; }
  .live-status.connected { color: #166534; border-color: #86efac; background: #f0fdf4; }
  .live-status.connecting, .live-status.reconnecting { color: #92400e; border-color: #fcd34d; background: #fffbeb; }
  .live-status.disconnected { color: #991b1b; border-color: #fca5a5; background: #fef2f2; }
  .sort-btn { border: 0; background: transparent; cursor: pointer; font: inherit; font-weight: 600; padding: 0; }
  .sort-btn.active { color: #1d4ed8; }
  .actions { display: flex; gap: 6px; flex-wrap: wrap; }
  .form { display: grid; gap: 8px; }
  .full { width: 100%; }
  .back { margin: 0 0 8px; }
`;
